/**
 * Present independent transfer status and controls for inbound connector feeds.
 *
 * Analysis belongs to the application dashboard. This component owns only
 * connector configuration, synchronization, reset, and disconnect actions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/services'
import { waitForVaultJob } from '@/subsystems/vault/services/vault-jobs'
import { connectorDefinition } from '@/subsystems/vault/connectors/registry'
import {
  listConnectorAnalyses,
  updateConnectorAnalysis,
} from '@/subsystems/vault/connectors/api'
import type { ConnectorAnalysisState, ConnectorAnalysisStatus, ConnectorPendingAction } from '@/subsystems/vault/connectors/types'
import { useAuth } from '@/subsystems/workspaces'

import { ResetWorkspaceDialog } from './ResetWorkspaceDialog'
import { FeedAnalysisCard } from './FeedAnalysisCard'

const ACTIVE_STATES = new Set<ConnectorAnalysisState>(['syncing', 'running', 'disconnecting'])
const ACTIVE_POLL_MS = 5_000
const IDLE_POLL_MS = 30_000
const NO_PENDING_ACTIONS: Readonly<Record<string, ConnectorPendingAction>> = {}

export function ConnectorAnalysisPanel({
  apiClient,
  orgId,
  workspaceId,
  onRefreshDocuments,
  onConfigureFeed,
  onDisconnectFeed,
  onPullRecords,
  pullableFeedIds,
  onRefreshSyncedRecords,
  onSyncFeed,
  canConfigureByProvider,
  canResetWorkspaceByProvider,
  queryAuthorityByProvider,
  pendingActions: externalPendingActions = NO_PENDING_ACTIONS,
}: {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  onRefreshDocuments: () => void | Promise<unknown>
  onConfigureFeed?: (feedId: string) => void
  onDisconnectFeed?: (feedId: string, sourceName: string) => void
  onPullRecords?: (feedId: string) => void
  pullableFeedIds?: readonly string[]
  onRefreshSyncedRecords?: (feedId: string) => Promise<string>
  onSyncFeed?: (feedId: string) => Promise<string>
  // Server-derived capability map; opens Configure for connector
  // administrators who hold no workspace role.
  canConfigureByProvider?: Record<string, boolean>
  // Server-derived destructive-action capability. Kuali grants this to its
  // connector administrators rather than ordinary workspace administrators.
  canResetWorkspaceByProvider?: Record<string, boolean>
  // Adapter-declared authority per provider, so a locked card names the
  // right unlock path: grant vs workspace administration.
  queryAuthorityByProvider?: Record<string, string>
  pendingActions?: Readonly<Record<string, ConnectorPendingAction>>
}) {
  const { isGlobalAdmin, isWorkspaceAdmin } = useAuth()
  const [localPendingActions, setLocalPendingActions] =
    useState<Record<string, ConnectorPendingAction>>({})
  const [resetFeed, setResetFeed] = useState<ConnectorAnalysisStatus>()
  const pendingActions = useMemo(
    () => ({ ...externalPendingActions, ...localPendingActions }),
    [externalPendingActions, localPendingActions],
  )
  const hasPendingActions = Object.keys(pendingActions).length > 0
  const setLocalPendingAction = useCallback((feedId: string, action?: ConnectorPendingAction) => {
    setLocalPendingActions((current) => {
      const next = { ...current }
      if (action) next[feedId] = action
      else delete next[feedId]
      return next
    })
  }, [])
  const analyses = useQuery({
    queryKey: ['connector-analyses', orgId, workspaceId],
    queryFn: () => listConnectorAnalyses(apiClient, orgId, workspaceId),
    enabled: Boolean(orgId && workspaceId),
    refetchInterval: query => hasPendingActions
      || query.state.data?.some(item => ACTIVE_STATES.has(item.state))
      ? ACTIVE_POLL_MS
      : IDLE_POLL_MS,
  })
  const active = hasPendingActions
    || (analyses.data?.some(item => ACTIVE_STATES.has(item.state)) ?? false)

  useEffect(() => {
    if (!active) return
    const timer = window.setInterval(() => void onRefreshDocuments(), ACTIVE_POLL_MS)
    return () => window.clearInterval(timer)
  }, [active, onRefreshDocuments])

  const reset = async () => {
    if (!resetFeed) return
    const feed = resetFeed
    setResetFeed(undefined)
    setLocalPendingAction(feed.feedId, 'reset')
    try {
      const response = await updateConnectorAnalysis(
        apiClient,
        orgId,
        workspaceId,
        feed.feedId,
        'reset',
      )
      await Promise.all([analyses.refetch(), onRefreshDocuments()])
      await Promise.all(response.jobIds.map(jobId => (
        waitForVaultJob(apiClient, orgId, workspaceId, jobId)
      )))
      await Promise.all([analyses.refetch(), onRefreshDocuments()])
      toast.success(`${feed.sourceName} workspace reset completed`)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? 'Could not reset connector workspace data')
    } finally {
      setLocalPendingAction(feed.feedId)
    }
  }

  const sync = async (analysis: ConnectorAnalysisStatus) => {
    if (!onSyncFeed) return
    setLocalPendingAction(analysis.feedId, 'sync')
    try {
      const jobId = await onSyncFeed(analysis.feedId)
      await waitForVaultJob(apiClient, orgId, workspaceId, jobId)
      await Promise.all([analyses.refetch(), onRefreshDocuments()])
      toast.success(`${analysis.sourceName} sync completed`)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? 'Could not sync connector')
    } finally {
      setLocalPendingAction(analysis.feedId)
    }
  }

  const refreshSynced = async (analysis: ConnectorAnalysisStatus) => {
    if (!onRefreshSyncedRecords) return
    setLocalPendingAction(analysis.feedId, 'sync')
    try {
      const jobId = await onRefreshSyncedRecords(analysis.feedId)
      await waitForVaultJob(apiClient, orgId, workspaceId, jobId)
      await Promise.all([analyses.refetch(), onRefreshDocuments()])
      toast.success(`${analysis.sourceName} synced records refreshed`)
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? 'Could not refresh synced records')
    } finally {
      setLocalPendingAction(analysis.feedId)
    }
  }

  if (!analyses.data?.length) return null

  return (
    <>
      <div data-testid="connector-analysis-panel" className="grid items-start gap-3.5 md:grid-cols-2">
        {analyses.data.map(analysis => {
          // Workspace admins keep every card action; a connector
          // administrator gets exactly Configure, and only on providers the
          // server-derived capability map grants them.
          const canAdministerWorkspace = isGlobalAdmin || isWorkspaceAdmin
          const canConfigure = canAdministerWorkspace
            || Boolean(canConfigureByProvider?.[analysis.provider])
          return (
          <FeedAnalysisCard
            key={analysis.feedId}
            analysis={analysis}
            canConfigure={canConfigure}
            canAdministerWorkspace={canAdministerWorkspace}
            lockedNotice={!canConfigure
              ? (queryAuthorityByProvider?.[analysis.provider] === 'workspace_admin'
                ? `${connectorDefinition(analysis.provider)?.label
                    ?? analysis.provider} uses personal sign-in and is `
                  + 'configured by workspace administrators.'
                : `You need a ${connectorDefinition(analysis.provider)?.label
                    ?? analysis.provider} connector administrator grant to `
                  + 'configure this connector. An organization admin can '
                  + 'grant it under User Management → Org Users.')
              : undefined}
            pendingAction={pendingActions[analysis.feedId]}
            onReset={canResetWorkspaceByProvider?.[analysis.provider]
              ? () => setResetFeed(analysis)
              : undefined}
            onConfigure={onConfigureFeed && canConfigure
              ? () => onConfigureFeed(analysis.feedId)
              : undefined}
            onDisconnect={onDisconnectFeed && canAdministerWorkspace
              ? () => onDisconnectFeed(analysis.feedId, analysis.sourceName)
              : undefined}
            onPullRecords={onPullRecords && canAdministerWorkspace
              && pullableFeedIds?.includes(analysis.feedId)
              ? () => onPullRecords(analysis.feedId)
              : undefined}
            onRefreshSyncedRecords={onRefreshSyncedRecords
              && canAdministerWorkspace
              && !analysis.automaticSyncEnabled
              && analysis.connectorDocuments > 0
              ? () => void refreshSynced(analysis)
              : undefined}
            onSync={onSyncFeed && canAdministerWorkspace
              && analysis.automaticSyncEnabled
              ? () => void sync(analysis)
              : undefined}
          />
          )
        })}
      </div>
      {resetFeed && (
        <ResetWorkspaceDialog
          open
          sourceName={resetFeed.sourceName}
          vaultName={resetFeed.destinationVaultName}
          onOpenChange={open => { if (!open) setResetFeed(undefined) }}
          onConfirm={() => void reset()}
        />
      )}
    </>
  )
}
